import {
    Card,
    CardContent,
    CardDetail,
    CardFront,
    CardMainAction,
    CardOpenIcon,
    CardStart,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import { NotebookPenIcon, PaperclipIcon } from 'lucide-react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { CrmNoteDTO } from '../../../server/dto/crm-note-dto.ts';
import { api } from '../../../utils/api.ts';
import { getNoteTypeLabel } from '../../notes/utils/get-note-type-label.ts';

type Props = {
    note: CrmNoteDTO;
    onOpen: () => void;
};

/**
 * The note logged on an event, as a summary: its text clamped to two lines, then attachments
 * and counts of what was logged with it. The full list lives in the note, which this opens.
 */
export function EventLoggedNoteSummary({ note, onOpen }: Props): ReactElement {
    const { t } = useTranslation('events');
    const { data: followUps } = api.platoUser.query.notes.getFollowUps.useQuery({ noteId: note.noteId });

    const title =
        note.interactionHappened === false
            ? note.noteType === 'PHONE_CALL'
                ? t('notes__type__phone_call_not_reached', { ns: 'notes' })
                : t('notes__type__visit_not_reached', { ns: 'notes' })
            : getNoteTypeLabel(note.noteType, t);

    const attachmentCount = note.attachments.length;
    const meta = [
        attachmentCount > 0 && t('card_protocol_attachments', { count: attachmentCount }),
        !!followUps?.tasks.length && t('card_protocol_tasks', { count: followUps.tasks.length }),
        !!followUps?.quotes.length && t('card_protocol_offers', { count: followUps.quotes.length }),
        !!followUps?.outcomes.length &&
            t('card_protocol_insights_triaged', { count: followUps.outcomes.length }),
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <Card level={3}>
            <CardFront>
                <CardStart render={<NotebookPenIcon />} />
                <CardContent className="min-w-0 gap-tight">
                    <CardTitle className="items-center">
                        {title}
                        <CardOpenIcon className="size-3.5 text-content-weaker" />
                    </CardTitle>
                    {note.title && <CardDetail className="line-clamp-2">{note.title}</CardDetail>}
                    {meta && (
                        <CardSubtitle className="order-0 text-ui-sm whitespace-normal @md:text-ui-sm">
                            {attachmentCount > 0 && <PaperclipIcon className="size-3 shrink-0" />}
                            {meta}
                        </CardSubtitle>
                    )}
                </CardContent>
                <CardMainAction onClick={onOpen} aria-label={t('card_protocol_open', { type: title })} />
            </CardFront>
        </Card>
    );
}
